import React, { useState, useEffect } from "react";
import { AuthLogo } from "@/components/Auth/AuthLogo";
import { AuthForm } from "@/components/Auth/AuthForm";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { Session } from "@supabase/supabase-js";

const signInSchema = z.object({
  email: z.string().trim().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        if (session?.user) {
          routeUser(session.user.id);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        routeUser(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const routeUser = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("onboarding_completed")
      .eq("id", userId)
      .maybeSingle();

    if (data?.onboarding_completed) {
      navigate("/dashboard");
    } else {
      navigate("/onboarding");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");

    const result = signInSchema.safeParse({ email, password });
    if (!result.success) {
      const errs = result.error.flatten().fieldErrors;
      if (errs.email) setEmailError(errs.email[0]);
      if (errs.password) setPasswordError(errs.password[0]);
      return;
    }

    setIsLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      toast.error("Invalid email or password");
      setIsLoading(false);
      return;
    }

    toast.success("Welcome back!");
    if (data?.user) routeUser(data.user.id);
    setIsLoading(false);
  };

  return (
    <main className="flex-grow px-6 flex flex-col items-center justify-center min-h-screen bg-background">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <AuthLogo />
        </div>

        <section className="flex flex-col items-center gap-2">
          <h1 className="text-2xl font-semibold">Welcome back</h1>
          <p className="text-muted-foreground text-center">
            Let's continue your learning journey.
          </p>

          <AuthForm
            view="signin"
            email={email}
            setEmail={setEmail}
            emailError={emailError}
            password={password}
            setPassword={setPassword}
            passwordError={passwordError}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            onForgotClick={() => navigate("/auth/forgotpassword")}
            onSubmit={handleSubmit}
            isLoading={isLoading}
          />

          <div className="mt-4 text-muted-foreground text-sm">
            Don't have an account?{" "}
            <Link to="/auth/signup" className="text-foreground underline">
              Sign up
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
